// Spawning graph windows. Extracted from App.tsx so windows that are not the
// main app (e.g. a trend GraphWindow opening a historical trend) can open a new
// graph window without importing the whole App module. App.tsx re-exports these
// for existing consumers (TigerViewPanel, action handlers, tests).

import { encodePrepopParam } from "./prepopTransfer";
import { appendLiveParam } from "./liveTabFetch";
import { getTigerResource } from "./tigerResource";

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

export type GraphMode = "graph" | "trend" | "event" | "multiTime" | "xy" | "xyTrend" | "xyTrendMulti";

/** Requested screen position + size for a spawned tool window (logical px).
 *  Used by tool-set loading to restore each saved dialog's rectangle. */
export interface WindowBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

const VALID_GRAPH_MODES: readonly GraphMode[] = [
  "graph", "trend", "event", "multiTime", "xy", "xyTrend", "xyTrendMulti",
] as const;

export function isGraphMode(s: string | null | undefined): s is GraphMode {
  return s != null && (VALID_GRAPH_MODES as readonly string[]).includes(s);
}

/**
 * Overlay saved legend column widths (the `?legendCols=` URL param, written by
 * tool-set loading from the C++ `channel_info_column_N` entries) onto the
 * window's default widths. The Tauri legend has an extra leading
 * visibility-checkbox column the C++ legend didn't have, so saved column N
 * lands on display column N+1; column 0 keeps its default. Bad or surplus
 * entries are ignored; widths clamp to the 20px resize minimum.
 */
/**
 * The opener's current turbine (`?config=<config>`), used as the channel dialog's
 * default selection for a tool window opened empty from the multi-turbine alarm
 * interface. Returns undefined when absent or empty. Mirrors the C++
 * `Ti_get_current_turbine()` default the desktop "Add Channel" dialog falls back
 * to when the graph has no plots.
 */
export function readDefaultConfigParam(): string | undefined {
  if (typeof window === "undefined") return undefined;
  const v = new URLSearchParams(window.location.search).get("config");
  return v && v.length > 0 ? v : undefined;
}

export function readLegendColsParam(defaults: number[]): number[] {
  if (typeof window === "undefined") return defaults;
  const raw = new URLSearchParams(window.location.search).get("legendCols");
  if (!raw) return defaults;
  const out = defaults.slice();
  raw.split(",").forEach((s, i) => {
    const w = parseInt(s, 10);
    const col = i + 1;
    if (Number.isFinite(w) && col < out.length) out[col] = Math.max(20, w);
  });
  return out;
}

export function openGraphWindow(
  mode: GraphMode = "graph",
  prepopParams?: string,
  bounds?: WindowBounds,
  legendCols?: number[],
  defaultConfig?: string,
) {
  // XY has its own HTML entry (separate bundle); trend/event/multiTime share graph.html
  // and select via ?mode=... on the URL. XY reuses graph-xy.html and uses
  // ?mode=trend / ?mode=trendMulti to pick the trend-channel + date-range path.
  const url = mode === "xy"
    ? "/graph-xy.html"
    : mode === "xyTrend"
      ? "/graph-xy.html?mode=trend"
      : mode === "xyTrendMulti"
        ? "/graph-xy.html?mode=trendMulti"
        : mode === "graph"
          ? "/graph.html"
          : `/graph.html?mode=${mode}`;
  let finalUrl = prepopParams
    ? url + (url.includes("?") ? "&" : "?") + encodePrepopParam(prepopParams)
    : url;
  // Saved legend column widths (tool-set loading); read back by
  // readLegendColsParam in the spawned window.
  if (legendCols && legendCols.length > 0) {
    finalUrl += (finalUrl.includes("?") ? "&" : "?") + "legendCols=" + legendCols.join(",");
  }
  // Carry the opener's current turbine so an empty graph's Add-Channel dialog
  // defaults to it (read back by readDefaultConfigParam) — the C++
  // Ti_get_current_turbine() default. No-op when not supplied.
  if (defaultConfig) {
    finalUrl += (finalUrl.includes("?") ? "&" : "?") + "config=" + encodeURIComponent(defaultConfig);
  }
  // Carry the opener's ?live=<config> so the graph window joins the same isolated,
  // turbine-scoped session (no-op on desktop / the plain alarm interface).
  finalUrl = appendLiveParam(finalUrl);

  // Window titles come from the C++ graph-title resources. Those values carry a
  // trailing space (the C++ appends the turbine name after them), so trim.
  const title = (mode === "trend" ? getTigerResource("TIGER_GRAPH_TREND_TITLE")
    : mode === "event" ? getTigerResource("TIGERUI_GRAPH_EVENT")
    : mode === "multiTime" ? getTigerResource("TIGER_GRAPH_MULTI_TITLE")
    : mode === "xy" ? getTigerResource("TIGER_GRAPH_XY_TITLE")
    : mode === "xyTrend" ? getTigerResource("TIGER_GRAPH_TRENDXY_TITLE")
    : mode === "xyTrendMulti" ? getTigerResource("TIGER_GRAPH_MULTI_TRENDXY_TITLE")
    : getTigerResource("TIGER_GRAPH_TITLE")).trim();
  console.log(`openGraphWindow called, isTauri=${isTauri()}, mode=${mode}, prepop=${prepopParams ? "yes" : "no"}`);
  if (isTauri()) {
    import("@tauri-apps/api/webviewWindow").then(({ WebviewWindow }) => {
      // Random suffix: tool-set loading can spawn several graphs in the same
      // millisecond, and window labels must be unique.
      const label = "graph-" + Date.now() + "-" + Math.random().toString(36).slice(2, 7);
      const wv = new WebviewWindow(label, {
        url: finalUrl,
        title,
        width: bounds?.width ?? 960,
        height: bounds?.height ?? 700,
        ...(bounds ? { x: bounds.x, y: bounds.y } : {}),
      });
      wv.once("tauri://created", () => console.log("Window created:", label));
      wv.once("tauri://error", (e) => console.error("Window error:", e));
    }).catch((err) => console.error("Import error:", err));
  } else {
    window.open(finalUrl, "_blank", "width=960,height=700,resizable=yes");
  }
}
